import {NgClass} from '@angular/common';
import {Component} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {EmTranslatePipe, validators} from '@emma-labs/shared/angular';
import {Language, LANGUAGE_OPTIONS} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {debounceTime, filter, switchMap} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {BrokerProfileService} from '../../../services/broker-profile/broker-profile.service';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';

interface ProfileForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  preferredLanguage: FormControl<Language>;
  email: FormControl<string>;
  assistantEmails: FormArray<FormControl<string>>;
  officeAddress: FormControl<string>;
  personalAddress: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'em-broker-information-section',
  templateUrl: './broker-information-section.component.html',
  imports: [ReactiveFormsModule, NgClass, DropdownModule, CardModule, InputTextModule, EmTranslatePipe],
})
export class BrokerInformationSectionComponent {
  readonly form: FormGroup<ProfileForm>;
  readonly languageOptions = LANGUAGE_OPTIONS;

  constructor(
    private readonly profileService: BrokerProfileService,
    private readonly fb: NonNullableFormBuilder,
  ) {
    this.form = this.createForm();

    this.profileService
      .get()
      .pipe(untilDestroyed(this))
      .subscribe((profile) => this.form.patchValue(profile, {emitEvent: false}));
  }

  private createForm(): FormGroup<ProfileForm> {
    const form = this.fb.group({
      firstName: ['', [Validators.maxLength(25), validators.noSpecialCharacter, validators.noNumber]],
      lastName: ['', [Validators.maxLength(25), validators.noSpecialCharacter, validators.noNumber]],
      preferredLanguage: [Language.UNKNOWN, [validators.language]],
      email: {value: '', disabled: true},
      assistantEmails: this.fb.array([['', validators.email]]),
      officeAddress: [''],
      personalAddress: [''],
    });

    form.valueChanges
      .pipe(
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter(() => form.valid),
        switchMap(() => this.profileService.update(this.form.getRawValue())),
        untilDestroyed(this),
      )
      .subscribe();

    return form;
  }
}
